import { useStore } from 'vuex'
import { computed, onMounted, ref } from 'vue'
import SortDropdown from '../SortDropdown/SortDropdown.vue'
import SpotCardGrid from '../SpotCardGrid/SpotCardGrid.vue'
import ProductCard from 'components/ProductCard/ProductCard.vue'
import MaterialCard from 'components/MaterialCard/MaterialCard.vue'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults.vue'

const urlMap = {
    products: 'PRODUCT_LIST_SEARCH',
    productsSearch: 'PRODUCT_SEARCH',
    materials: 'PRODUCT_LIST_SEARCH',
    materialsSearch: 'PRODUCT_SEARCH'
}

const frivilligUrlMap = {
    products: 'FRIVILLIG_PRODUCT_LIST_SEARCH',
    productsSearch: 'FRIVILLIG_PRODUCT_SEARCH',
    materials: 'FRIVILLIG_PRODUCT_LIST_SEARCH',
    materialsSearch: 'FRIVILLIG_PRODUCT_SEARCH'
}

const getUrl = function (type, webshopContext) {
    if (webshopContext === 'frivilligwebshop'){
        return frivilligUrlMap[type] ?? 'FRIVILLIG_PRODUCT_SEARCH'
    }

    return urlMap[type] ?? 'PRODUCT_SEARCH'
}

const webshopSearch = function (props, store) {
    store.dispatch('search/initSearchScope', {
        guid: props.guid,
        firstPageSize: props.firstPageSize,
        showMorePageSize: props.showMorePageSize,
        url: process.env[getUrl(props.type, props.webshopContext)]
    })

    const inputText = new URLSearchParams(document.location.search).get('q')
    const skip = new URLSearchParams(document.location.search).get('skip')
    const take =
        new URLSearchParams(document.location.search).get('take') ?? props.take

    store.dispatch('search/performSearch', {
        guid: props.guid,
        query: inputText,
        skip,
        take
    })
}

export default {
    name: 'ProductList',
    components: {
        SortDropdown,
        SpotCardGrid,
        ProductCard,
        MaterialCard,
        NoSearchResults
    },
    props: {
        guid: {
            type: String,
            required: true
        },
        type: {
            type: String,
            required: false,
            default: 'products'
        },
        firstPageSize: {
            type: Number,
            required: false
        },
        showMorePageSize: {
            type: Number,
            required: false
        },
        avoidSetQueryInUrl: {
            type: Boolean,
            required: false,
            default: false
        },
        hideNoResults: {
            type: Boolean,
            required: false,
            default: false
        },
        webshopContext: {
            type: String,
            required: false,
            default: 'webshop'
        }
    },
    setup(props) {
        const store = useStore()
        const results = computed(
            () => store.state.search.results[props.guid] ?? []
        )

        const spotCardCount = computed(() => {
            return results.value.filter(r => r.isSpotCard).length
        })

        const totalCount = computed(
            () => store.state.search.scoped_total[props.guid] - spotCardCount.value ?? 0
        )

        const resultCount = computed(() => {
            if (displayProperties.gridType === 'products') {
                let totalCount = 0

                for (const key in results.value) {
                    if (results.value.hasOwnProperty(key)) {
                        const array = results.value[key].entries
                        totalCount += array.length
                    }
                }

                return totalCount
            }

            return results.value.length - spotCardCount.value
        })

        const showSpinner = computed(
            () => store.state.search.search_inprogress[props.guid]
        )

        const determineLabelCollection = function () {
            return (
                {
                    materialsSearch: 'webshopSearchMaterials',
                    productsSearch: 'webshopSearchProducts',
                    products: 'productList',
                    materials: 'productList'
                }[props.type] ??
                console.error(
                    'search boolean or type missing in order to determine labelCollection'
                )
            )
        }

        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: determineLabelCollection(),
                labelKey: labelKey
            })

        const getMoreResults = function () {
            store.dispatch('search/performMoreSearch', {
                guid: props.guid
            })
        }

        onMounted(() => {
            if (props.avoidSetQueryInUrl) {
                store.commit('search/set_replaceQueryInUrl', false)
            }
            webshopSearch(props, store)
        })

        const sortingHandler = function (sorting) {
            store.dispatch('search/performSearch', {
                guid: props.guid,
                sorting
            })
        }

        const canShowMore = computed(() =>
            store.getters['search/canShowMore'](props.guid)
        )

        const displayProperties = {
            products: { card: 'ProductCard', gridType: '3col' },
            productsSearch: { card: 'ProductCard', gridType: '3col' },
            materials: { card: 'MaterialCard', gridType: '4col' },
            materialsSearch: { card: 'MaterialCard', gridType: '4col' }
        }[props.type] ?? { card: 'ProductCard' }

        const displayModifications = computed(() => {
            const grid = 'kb-product-list__results--grid'
            const gridModifications = displayProperties.gridType
                ? displayProperties.gridType
                    ? grid + '-' + displayProperties.gridType
                    : ''
                : null

            return gridModifications ? gridModifications : ''
        })

        const totalCountLabel = computed(() =>
            getLabel('resultCountTitle').replace('{}', totalCount.value)
        )

        return {
            displayProperties,
            results,
            resultCount,
            totalCountLabel,
            getLabel,
            getMoreResults,
            showSpinner,
            totalCount,
            canShowMore,
            sortingHandler,
            displayModifications,
        }
    }
}
